import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Bell, Check, Trash2, ExternalLink, AlertTriangle, 
  CheckCircle, Info, AlertCircle, X 
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const POLLING_INTERVAL = 30000; // 30 secondes

export function NotificationBell() {
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [errorCount, setErrorCount] = useState(0);
  const [recentNotifications, setRecentNotifications] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const lastErrorCount = useRef(0);

  const getToken = () => localStorage.getItem('admin_token');

  // Permission pour les notifications push
  const requestPushPermission = useCallback(async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        console.log('Push notifications enabled');
      }
    }
  }, []);

  // Envoyer une notification push (uniquement pour les erreurs)
  const sendPushNotification = useCallback((notification) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('Mutagim Admin - Alerte', {
        body: notification.message,
        icon: '/favicon.ico',
        tag: notification.id,
        requireInteraction: true,
      });
    }
  }, []);

  // Fetch unread count (polling) and recent notifications
  const fetchUnreadCount = useCallback(async () => {
    try {
      const token = getToken();
      if (!token) return;

      // Fetch recent notifications (10 dernières) pour le dropdown
      const recentResponse = await fetch(`${API_URL}/api/admin/notifications/recent?limit=10`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (recentResponse.ok) {
        const recentData = await recentResponse.json();
        setRecentNotifications(recentData.notifications || []);
        setUnreadCount(recentData.unread_count || 0);
        
        // Fetch error count séparément
        const unreadResponse = await fetch(`${API_URL}/api/admin/notifications/unread-count`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        
        if (unreadResponse.ok) {
          const unreadData = await unreadResponse.json();
          
          // Si nouvelles erreurs, envoyer push notification
          if (unreadData.error_count > lastErrorCount.current) {
            const newErrors = (unreadData.recent || []).filter(n => 
              n.severity === 'error' && !n.read
            );
            newErrors.forEach(sendPushNotification);
          }
          lastErrorCount.current = unreadData.error_count;
          setErrorCount(unreadData.error_count);
        }
      }
    } catch (error) {
      console.error('Notification polling error:', error);
    }
  }, [sendPushNotification]);

  // Polling toutes les 30 secondes
  useEffect(() => {
    fetchUnreadCount();
    requestPushPermission();
    
    const interval = setInterval(fetchUnreadCount, POLLING_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchUnreadCount, requestPushPermission]);

  // Fermer dropdown si clic extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const markAsRead = async (ids = null, all = false) => {
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/admin/notifications/mark-read`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(all ? { all: true } : { ids }),
      });

      if (response.ok) {
        fetchUnreadCount();
      }
    } catch (error) {
      console.error('Mark read error:', error);
    }
  };

  const handleNotificationClick = (notification) => {
    markAsRead([notification.id]);
    if (notification.link) {
      navigate(notification.link);
    }
    setIsOpen(false);
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'error':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
      case 'success':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      default:
        return <Info className="w-4 h-4 text-blue-500" />;
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = (now - date) / 1000;
    
    if (diff < 60) return 'À l\'instant';
    if (diff < 3600) return `Il y a ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `Il y a ${Math.floor(diff / 3600)}h`;
    return date.toLocaleDateString('fr-FR');
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setIsOpen(!isOpen)}
        className="relative text-zinc-400 hover:text-white"
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span 
            className={`absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center text-xs font-bold rounded-full ${
              errorCount > 0 ? 'bg-red-500' : 'bg-[#C0000C]'
            } text-white`}
            data-testid="notification-badge"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </Button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800">
            <h3 className="text-white font-semibold">Notifications</h3>
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => markAsRead(null, true)}
                className="text-xs text-zinc-400 hover:text-white"
              >
                Tout marquer lu
              </Button>
            )}
          </div>

          {/* Notifications List */}
          <div className="max-h-[400px] overflow-y-auto">
            {recentNotifications.length === 0 ? (
              <div className="py-8 text-center text-zinc-500">
                <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>Aucune notification</p>
              </div>
            ) : (
              recentNotifications.map((notif) => (
                <div
                  key={notif.id}
                  onClick={() => handleNotificationClick(notif)}
                  className={`px-4 py-3 border-b border-zinc-800 cursor-pointer hover:bg-zinc-800/50 transition-colors ${
                    !notif.read ? 'bg-zinc-800/30' : ''
                  }`}
                  data-testid={`notification-${notif.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5">
                      {getSeverityIcon(notif.severity)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${!notif.read ? 'text-white font-medium' : 'text-zinc-400'}`}>
                        {notif.message}
                      </p>
                      <p className="text-xs text-zinc-500 mt-1">
                        {formatTime(notif.created_at)}
                      </p>
                    </div>
                    {notif.link && (
                      <ExternalLink className="w-4 h-4 text-zinc-600 flex-shrink-0" />
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-3 border-t border-zinc-800">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { navigate('/admin/notifications'); setIsOpen(false); }}
              className="w-full text-zinc-400 hover:text-white"
            >
              Voir toutes les notifications
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificationBell;
